import { apiPaginated, apiRequest } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiData } from "@/api/types";

export const adminService = {
  dashboard: () => apiRequest<ApiData>(endpoints.admin.dashboard),
  users: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.users, { query: params }),
  user: (id: string) => apiRequest<ApiData>(endpoints.admin.user(id)),
  roles: () => apiRequest<ApiData[]>(endpoints.admin.roles),
  permissions: () => apiRequest<ApiData>(endpoints.admin.permissions),
  teams: () => apiRequest<ApiData[]>(endpoints.admin.teams),
  players: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.players, { query: params }),
  player: (id: string) => apiRequest<ApiData>(endpoints.admin.player(id)),
  staff: () => apiRequest<ApiData[]>(endpoints.admin.staff),
  staffMember: (id: string) => apiRequest<ApiData>(endpoints.admin.staffMember(id)),
  fixtures: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.fixtures, { query: params }),
  competitions: () => apiRequest<ApiData[]>(endpoints.admin.competitions),
  seasons: () => apiRequest<ApiData[]>(endpoints.admin.seasons),
  venues: () => apiRequest<ApiData[]>(endpoints.admin.venues),
  streams: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.streams, { query: params }),
  stream: (id: string) => apiRequest<ApiData>(endpoints.admin.stream(id)),
  streamIncidents: (id: string) => apiRequest<ApiData[]>(endpoints.admin.streamIncidents(id)),
  articles: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.articles, { query: params }),
  media: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.media, { query: params }),
  galleries: () => apiRequest<ApiData[]>(endpoints.admin.galleries),
  sponsors: () => apiRequest<ApiData[]>(endpoints.admin.sponsors),
  forms: () => apiRequest<ApiData[]>(endpoints.admin.forms),
  enquiries: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.enquiries, { query: params }),
  newsletterSubscribers: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.newsletterSubscribers, { query: params }),
  updateNewsletterSubscriber: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.newsletterSubscriber(id), { method: "PATCH", body }),
  reports: () => apiRequest<ApiData>(endpoints.admin.reports),
  analytics: () => apiRequest<ApiData>(endpoints.admin.analytics),
  auditLogs: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.auditLogs, { query: params }),
  settings: () => apiRequest<ApiData>(endpoints.admin.settings),
  createTeam: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.teams, { method: "POST", body }),
  updateTeam: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.team(id), { method: "PATCH", body }),
  archiveTeam: (id: string) =>
    apiRequest<{ ok: true }>(endpoints.admin.teamArchive(id), { method: "POST" }),
  createStaff: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.staff, { method: "POST", body }),
  updateStaff: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.staffMember(id), { method: "PATCH", body }),
  deleteStaff: (id: string, deleteMedia = false) =>
    apiRequest<{ ok: true }>(endpoints.admin.staffMember(id), {
      method: "DELETE",
      body: { deleteMedia },
    }),
  updateSettings: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.settings, { method: "PUT", body }),
  homepageBlocks: () => apiRequest<ApiData[]>(endpoints.admin.homepageBlocks),
  createHomepageBlock: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.homepageBlocks, { method: "POST", body }),
  updateHomepageBlock: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.homepageBlock(id), { method: "PATCH", body }),
  reorderHomepageBlocks: (publicIds: string[]) =>
    apiRequest<ApiData>(endpoints.admin.homepageReorder, {
      method: "PUT",
      body: { publicIds },
    }),
  profileChangeRequests: (params?: Record<string, unknown>) =>
    apiPaginated<ApiData>(endpoints.admin.profileChangeRequests, { query: params }),
  reviewProfileChange: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.profileChangeRequest(id), { method: "PATCH", body }),
  createUser: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.users, { method: "POST", body }),
  updateUser: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.user(id), { method: "PATCH", body }),
  updateRole: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.role(id), { method: "PUT", body }),
  createRole: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.roles, { method: "POST", body }),
  createPlayer: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.players, { method: "POST", body }),
  updatePlayer: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.player(id), { method: "PATCH", body }),
  createFixture: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.fixtures, { method: "POST", body }),
  updateFixture: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.fixture(id), { method: "PATCH", body }),
  createStream: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.streams, { method: "POST", body }),
  updateStream: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.stream(id), { method: "PATCH", body }),
  updateStreamStatus: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.streamStatus(id), { method: "PATCH", body }),
  testStream: (id: string) =>
    apiRequest<ApiData>(endpoints.admin.streamTest(id), { method: "POST" }),
  createStreamIncident: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.streamIncidents(id), { method: "POST", body }),
  createArticle: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.articles, { method: "POST", body }),
  updateArticle: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.article(id), { method: "PATCH", body }),
  publishArticle: (id: string) =>
    apiRequest<ApiData>(endpoints.admin.articlePublish(id), { method: "POST" }),
  uploadMedia: (formData: FormData) =>
    apiRequest<ApiData>(endpoints.admin.mediaUpload, { method: "POST", formData }),
  createGallery: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.galleries, { method: "POST", body }),
  updateGallery: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.gallery(id), { method: "PATCH", body }),
  deleteGallery: (id: string) =>
    apiRequest<{ ok: true }>(endpoints.admin.gallery(id), { method: "DELETE" }),
  createSponsor: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.sponsors, { method: "POST", body }),
  updateSponsor: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.admin.sponsor(id), { method: "PATCH", body }),
};

export const playerService = {
  dashboard: () => apiRequest<ApiData>(endpoints.player.dashboard),
  profile: () => apiRequest<ApiData>(endpoints.player.profile),
  fixtures: () => apiRequest<ApiData[]>(endpoints.player.fixtures),
  results: () => apiRequest<ApiData[]>(endpoints.player.results),
  statistics: () => apiRequest<ApiData>(endpoints.player.statistics),
  trainingSessions: () => apiRequest<ApiData[]>(endpoints.player.trainingSessions),
  attendance: () => apiRequest<ApiData[]>(endpoints.player.attendance),
  documents: () => apiRequest<ApiData[]>(endpoints.player.documents),
  videos: () => apiRequest<ApiData[]>(endpoints.player.videos),
  announcements: () => apiRequest<ApiData[]>(endpoints.player.announcements),
  notifications: () => apiRequest<ApiData[]>(endpoints.player.notifications),
  requestProfileChange: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.player.profileChangeRequests, { method: "POST", body }),
  notificationPreferences: () => apiRequest<ApiData>(endpoints.player.notificationPreferences),
  updateNotificationPreferences: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.player.notificationPreferences, { method: "PUT", body }),
};

export const coachingService = {
  dashboard: () => apiRequest<ApiData>(endpoints.coaching.dashboard),
  trainingSessions: () => apiRequest<ApiData[]>(endpoints.coaching.trainingSessions),
  matchSelections: () => apiRequest<ApiData[]>(endpoints.coaching.matchSelections),
  playerAssessments: () => apiRequest<ApiData[]>(endpoints.coaching.playerAssessments),
  reports: () => apiRequest<ApiData>(endpoints.coaching.reports),
  createTrainingSession: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.coaching.trainingSessions, { method: "POST", body }),
  recordAttendance: (id: string, body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.coaching.trainingAttendance(id), { method: "PUT", body }),
  createAssessment: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.coaching.playerAssessments, { method: "POST", body }),
  saveSelection: (body: Record<string, unknown>) =>
    apiRequest<ApiData>(endpoints.coaching.matchSelections, { method: "PUT", body }),
};
